/* eslint-disable react-refresh/only-export-components */
/**
  Modified version of https://github.com/icd2k3/use-react-router-breadcrumbs
 *
 */
import React, { createElement } from "react";
import {
  matchPath,
  useLocation,
  RouteObject,
  Params,
  Route,
  PathRouteProps,
  LayoutRouteProps,
  IndexRouteProps,
  PathMatch
} from "react-router-dom";

type Location = ReturnType<typeof useLocation>;

interface PathPattern<Path extends string = string> {
  path: Path;
  caseSensitive?: boolean;
  end?: boolean;
}
//todo : previous' type def
export interface Options {
  disableDefaults?: boolean;
  excludePaths?: string[];
  defaultFormatter?: (str: string) => string;
  previous?: any;
  current?: any;
  parent?: any;
}

export interface BreadcrumbMatch<ParamKey extends string = string> {
  /**
   * The names and values of dynamic parameters in the URL.
   */
  params: Params<ParamKey>;
  /**
   * The portion of the URL pathname that was matched.
   */
  pathname: string;
  /**
   * The pattern that was used to match.
   */
  pattern: PathPattern;
  /**
   * The route object that was used to match.
   */
  route?: BreadcrumbsRoute;

  referrer?: string | null;
}

export interface BreadcrumbComponentProps<ParamKey extends string = string> {
  key: string;
  match: BreadcrumbMatch<ParamKey>;
  location: Location;
  [x: string]: unknown;
}

export type BreadcrumbComponentType<ParamKey extends string = string> =
  React.ComponentType<BreadcrumbComponentProps<ParamKey>>;

export type BreadcrumbsRoute<ParamKey extends string = string> = RouteObject & {
  children?: BreadcrumbsRoute[];
  breadcrumb?: BreadcrumbComponentType<ParamKey> | string | null;
  props?: { [x: string]: unknown };
  hierarchy?: { path: string }[];
  key?: string;
  ignoreWithChildren?: boolean;
};

export interface BreadcrumbData<ParamKey extends string = string> {
  match: BreadcrumbMatch<ParamKey>;
  location: Location;
  key: string;
  breadcrumb: React.ReactNode;
}

// The code below is modified from React Router
interface BreadcrumbsRouteMeta {
  relativePath: string;
  childrenIndex: number;
  route: BreadcrumbsRoute;
}

interface BreadcrumbsRouteBranch {
  path: string;
  key: string;
  score: number;
  routesMeta: BreadcrumbsRouteMeta[];
}

const joinPaths = (paths: string[]): string =>
  paths.join("/").replace(/\/\/+/g, "/");

const paramRe = /^:\w+$/;
const dynamicSegmentValue = 3;
const indexRouteValue = 2;
const emptySegmentValue = 1;
const staticSegmentValue = 10;
const splatPenalty = -2;
const isSplat = (s: string) => s === "*";

function computeScore(path: string, index: boolean | undefined): number {
  const segments = path.split("/");
  let initialScore = segments.length;
  if (segments.some(isSplat)) {
    initialScore += splatPenalty;
  }

  if (index) {
    initialScore += indexRouteValue;
  }

  return segments
    .filter((s) => !isSplat(s))
    .reduce((score, segment) => {
      if (paramRe.test(segment)) {
        return score + dynamicSegmentValue;
      }
      if (segment === "") {
        return score + emptySegmentValue;
      }
      return score + staticSegmentValue;
    }, initialScore);
}

function compareIndexes(a: number[], b: number[]): number {
  const siblings =
    a.length === b.length && a.slice(0, -1).every((n, i) => n === b[i]);
  return siblings ? a[a.length - 1] - b[b.length - 1] : 0;
}

function flattenRoutes(
  routes: BreadcrumbsRoute[],
  branches: BreadcrumbsRouteBranch[] = [],
  parentsMeta: BreadcrumbsRouteMeta[] = [],
  parentPath = ""
): BreadcrumbsRouteBranch[] {
  routes.forEach((route, index) => {
    if (
      typeof route.path !== "string" &&
      !route.index &&
      !route.children?.length
    ) {
      throw new Error(
        "useBreadcrumbs: `path` or `index` must be provided in every route object"
      );
    }
    if (route.path && route.index) {
      throw new Error(
        "useBreadcrumbs: `path` and `index` cannot be provided at the same time"
      );
    }
    const meta: BreadcrumbsRouteMeta = {
      relativePath: route.path || "",
      childrenIndex: index,
      route
    };

    if (meta.relativePath.charAt(0) === "/") {
      if (!meta.relativePath.startsWith(parentPath)) {
        throw new Error(
          "useBreadcrumbs: The absolute path of the child route must start with the parent path"
        );
      }
      meta.relativePath = meta.relativePath.slice(parentPath.length);
    }

    const path = joinPaths([parentPath, meta.relativePath]);
    const routesMeta = parentsMeta.concat(meta);

    if (route.children && route.children.length > 0) {
      if (route.index) {
        throw new Error("useBreadcrumbs: Index route cannot have child routes");
      }
      flattenRoutes(route.children, branches, routesMeta, path);
    }
    const key = route.key || "";

    branches.push({
      path,
      key,
      score: computeScore(path, route.index),
      routesMeta
    });
  });
  return branches;
}

function rankRouteBranches(
  branches: BreadcrumbsRouteBranch[]
): BreadcrumbsRouteBranch[] {
  return branches.sort((a, b) =>
    a.score !== b.score
      ? b.score - a.score // Higher score first
      : compareIndexes(
          a.routesMeta.map((meta) => meta.childrenIndex),
          b.routesMeta.map((meta) => meta.childrenIndex)
        )
  );
}

const NO_BREADCRUMB = Symbol("NO_BREADCRUMB");

const humanize = (str: string): string =>
  str
    .replace(/^[\s_]+|[\s_]+$/g, "")
    .replace(/[-_\s]+/g, " ")
    .replace(/^[a-z]/, (m) => m.toUpperCase());

const render = ({
  breadcrumb: Breadcrumb,
  match,
  location,
  props
}: {
  breadcrumb: BreadcrumbComponentType | string;
  match: BreadcrumbMatch;
  location: Location;
  props?: { [x: string]: unknown };
}): BreadcrumbData => {
  const componentProps = {
    match,
    location,
    key: match.pathname,
    ...(props || {})
  };

  return {
    ...componentProps,
    breadcrumb:
      typeof Breadcrumb === "string" ? (
        createElement("span", { key: componentProps.key }, Breadcrumb)
      ) : (
        <Breadcrumb {...componentProps} />
      )
  };
};

/**
 * Small helper method to get a default breadcrumb if the user hasn't provided one.
 */
const getDefaultBreadcrumb = ({
  currentSection,
  location,
  pathSection,
  defaultFormatter
}: {
  currentSection: string;
  location: Location;
  pathSection: string;
  defaultFormatter?: (str: string) => string;
}): BreadcrumbData => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const match = matchPath(
    {
      end: true,
      path: pathSection
    },
    pathSection
  )!;

  return render({
    breadcrumb: defaultFormatter
      ? defaultFormatter(currentSection)
      : humanize(currentSection),
    match,
    location
  });
};

/**
 * Loops through the route array (if provided) and returns either a
 * user-provided breadcrumb OR a sensible default (if enabled)
 */

// todo. change some to for of */
const getBreadcrumbMatch = ({
  currentSection,
  referrerParam,
  disableDefaults,
  excludePaths,
  defaultFormatter,
  location,
  pathSection,
  branches,
  isPrev
}: {
  currentSection: string;
  referrerParam?: string | null;
  disableDefaults?: boolean;
  excludePaths?: string[];
  defaultFormatter?: (str: string) => string;
  location: Location;
  pathSection: string;
  branches: BreadcrumbsRouteBranch[];
  isPrev?: boolean;
}): typeof NO_BREADCRUMB | BreadcrumbData => {
  let breadcrumb: BreadcrumbData | typeof NO_BREADCRUMB | undefined;

  // Check the optional `excludePaths` option in `options` to see if the
  // current path should not include a breadcrumb.
  const getIsPathExcluded = (path: string): boolean =>
    matchPath(
      {
        path,
        end: true
      },
      pathSection
    ) != null;

  if (excludePaths && excludePaths.some(getIsPathExcluded)) {
    return NO_BREADCRUMB;
  }

  // Loop through the route array and see if the user has provided a custom breadcrumb.
  branches.some(({ path, routesMeta }) => {
    const { route } = routesMeta[routesMeta.length - 1];
    let userProvidedBreadcrumb = route.breadcrumb;
    // If the route is an index, but no breadcrumb is set,
    // We try to use the breadcrumbs of the parent route instead

    if (!userProvidedBreadcrumb && route.index) {
      const parentMeta = routesMeta[routesMeta.length - 2];

      if (parentMeta && parentMeta.route.breadcrumb) {
        userProvidedBreadcrumb = parentMeta.route.breadcrumb;
      }
    }

    const { caseSensitive, props, ignoreWithChildren } = route;
    const match = matchPath(
      {
        path,
        end: true,
        caseSensitive
      },
      pathSection
    );

    // If user passed breadcrumb: null
    // we need to know NOT to add it to the matches array
    // see: `if (breadcrumb !== NO_BREADCRUMB)` below.

    if (match) {
      // This covers the case where a user may be extending their react-router route
      // config with breadcrumbs, but also does not want default breadcrumbs to be
      // automatically generated (opt-in).
      if (match && userProvidedBreadcrumb === null) {
        breadcrumb = NO_BREADCRUMB;
        return true;
      }

      if (!userProvidedBreadcrumb && disableDefaults) {
        breadcrumb = NO_BREADCRUMB;
        return true;
      }
      if (ignoreWithChildren && isPrev) {
        breadcrumb = NO_BREADCRUMB;
        return true;
      }

      breadcrumb = render({
        // Although we have a match, the user may be passing their react-router config object
        // which we support. The route config object may not have a `breadcrumb` param specified.
        // If this is the case, we should provide a default via `humanize`.
        breadcrumb:
          userProvidedBreadcrumb ||
          (defaultFormatter
            ? defaultFormatter(currentSection)
            : humanize(currentSection)),
        match: { ...match, route, referrer: referrerParam },
        location,
        props
      });
      return true;
    }
    return false;
  });

  // User provided a breadcrumb prop, or we generated one above.
  if (breadcrumb) {
    // console.log({ breadcrumb }, "in m");

    return breadcrumb;
  }

  // If there was no breadcrumb provided and user has disableDefaults turned on.
  if (disableDefaults) {
    return NO_BREADCRUMB;
  }

  // If the above conditionals don't fire, generate a default breadcrumb based on the path.
  return getDefaultBreadcrumb({
    pathSection,
    // include a "Home" breadcrumb by default (can be overrode or disabled in config).
    currentSection: pathSection === "/" ? "Home" : currentSection,
    location,
    defaultFormatter
  });
};

const checkHierarchy = (
  pathSection: string,
  routes: BreadcrumbsRoute[],
  branches: any,
  options: Options,
  location: Location,
  parent: any
) => {
  let breadcrumbs: any = [];

  for (const branch of branches) {
    const { path } = branch;
    const match = matchPath(
      {
        end: true,
        path: path
      },
      pathSection
    );

    if (match && parent?.path) {
      let parentMatch: PathMatch<string> | null = null;
      for (let j = 0; j < branches?.length; j++) {
        const { path } = branches[j];
        const _matchPath = matchPath(
          {
            end: true,
            path: path
          },
          parent.path
        );

        if (_matchPath) {
          parentMatch = _matchPath;
          break;
        }
      }
      if (parentMatch && Object.keys(parentMatch).length > 0) {
        const historyPath: string = parentMatch["pathname"] || "";
        breadcrumbs = getBreadcrumbs({
          pathname: historyPath,
          referrerParam: parent.referrer,
          routes,
          location,
          options,
          recursion: true
        });

        breadcrumbs = breadcrumbs.slice(-1);
        if (parent.parent) {
          // const parentOptions: Options = { ...options, parent: parent};
          const parentBreadcrumb = checkHierarchy(
            historyPath,
            routes,
            branches,
            options,
            location,
            parent.parent
          );
          breadcrumbs = breadcrumbs.slice(-1);
          breadcrumbs.push(...parentBreadcrumb);
          breadcrumbs.reverse();
        }
      }
      // console.log({ breadcrumbs });

      return breadcrumbs;
    }
  }
};
const checkReferrel = (
  routes: BreadcrumbsRoute[],
  branches: any,
  options: Options,
  location: Location
) => {
  const url = new URLSearchParams(window.location.search);
  let referrerParams: string[] = url.getAll("ref") || [];
  referrerParams = referrerParams.filter((item) => item !== "");
  const breadcrumbs: any = [];

  if (!referrerParams || referrerParams.length === 0) return false;

  for (const refparam of referrerParams) {
    const br = branches.find(
      (route: BreadcrumbsRoute) =>
        route.key && route.key !== "" && route.key === refparam
    );

    if (br?.path) {
      let referrerMatch: PathMatch<string> | null = null;
      for (const branch of branches) {
        const path = branch?.path;

        const match = matchPath({ end: true, path }, br.path as string);
        if (match) {
          referrerMatch = match;
          break;
        }
      }

      if (referrerMatch && Object.keys(referrerMatch).length > 0) {
        const referrerPath: string = referrerMatch["pathname"] || "";

        const _breadcrumb = getBreadcrumbs({
          pathname: referrerPath,
          referrerParam: null /* not the same as referrerParams */,
          routes,
          location,
          options,
          recursion: true
        });

        if (_breadcrumb.length > 0) {
          breadcrumbs.push(_breadcrumb[_breadcrumb.length - 1]);
        }
      }
    }
  }
  return breadcrumbs;
};

/**
 * Splits the pathname into sections, then search for matches in the routes
 * a user-provided breadcrumb OR a sensible default.
 */
const getBreadcrumbs = ({
  pathname,
  referrerParam = null,
  routes,
  location,
  options = {},
  recursion = false
}: {
  pathname: string;
  routes: BreadcrumbsRoute[];
  location: Location;
  options: Options;
  referrerParam: string | null;
  recursion?: boolean;
}): BreadcrumbData[] => {
  const branches = rankRouteBranches(flattenRoutes(routes));
  let hierarchyBreadcrumb: any;
  let referrerBreadcrumb: any;
  const breadcrumbs: BreadcrumbData[] = [];
  pathname
    .split("?")[0]
    .split("/")
    .reduce(
      (
        previousSection: string,
        currentSection: string,
        index: number,
        array
      ) => {
        // Combine the last route section with the currentSection.
        // For example, `pathname = /1/2/3` results in match checks for
        // `/1`, `/1/2`, `/1/2/3`.

        const pathSection = !currentSection
          ? "/"
          : `${previousSection}/${currentSection}`;
        // Ignore trailing slash or double slashes in the URL
        if (pathSection === "/" && index !== 0) {
          return "";
        }

        const isPrev = index < array.length - 1 && index !== 0;
        const parent = options?.current?.parent || {};
        const breadcrumb = getBreadcrumbMatch({
          currentSection,
          referrerParam,
          location,
          pathSection,
          branches,
          isPrev,
          ...options
        });

        /* on the last iteration, so we can append before lastcrumb */
        /* this block will not execute when the function is called from 
            referrerBreadcrumb and hierarchyBreadcrumb */
        if (index === array.length - 1 && !recursion) {
          referrerBreadcrumb = checkReferrel(
            routes,
            branches,
            options,
            location
          );
          hierarchyBreadcrumb = checkHierarchy(
            pathSection,
            routes,
            branches,
            options,
            location,
            parent
          );
        }

        // Add the breadcrumb to the matches array
        // unless the user has explicitly passed.
        // { path: x, breadcrumb: null } to disable.
        // supplying pathname in this condition to not render crumb for home page
        if (breadcrumb !== NO_BREADCRUMB && pathname !== "/") {
          if (referrerBreadcrumb && referrerBreadcrumb.length > 0) {
            breadcrumbs.splice(1, 0, ...referrerBreadcrumb);
          }
          if (hierarchyBreadcrumb && hierarchyBreadcrumb.length > 0) {
            breadcrumbs.push(...hierarchyBreadcrumb);
            // breadcrumbs.push(hierarchyBreadcrumb);
          }
          breadcrumbs.push(breadcrumb);
        }

        return pathSection === "/" ? "" : pathSection;
      },
      ""
    );

  return breadcrumbs;
};

/**
 * Default hook function export.
 */
const useReactRouterBreadcrumbs = (
  routes: BreadcrumbsRoute[],
  options: Options
): BreadcrumbData[] => {
  const { pathname } = location;

  return getBreadcrumbs({
    pathname,
    referrerParam: null,
    routes: routes || [],
    location: useLocation(),
    options,
    recursion: false
  });
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BreadCrumb = {
  breadcrumb?: string | ((param: any) => JSX.Element) | JSX.Element | null;
};

type BreadCrumbRouteType = (
  _props: (PathRouteProps | LayoutRouteProps | IndexRouteProps) & BreadCrumb
) => React.ReactElement | null;

const BreadCrumbRoute: BreadCrumbRouteType = Route;

export default useReactRouterBreadcrumbs;

export { BreadCrumbRoute as Route };
